import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { DCC_INSTALL_STEPS, DCC_SMARTSCREEN_IMG, DCC_SMARTSCREEN_CAPTION } from './dccInstallGuide';

// Dashboard DCC overlay — VISINVOKE locked TWO-OUTCOME flow.
//
// The decision is based ONLY on the account state user.onboarding.dcc_selected, read from
// /v1/auth/me. The VISINVOKE account is the source of truth: there is no browser-local state,
// no dcc_downloaded lifecycle branch, and no redirect into the desktop application.
//
//   dcc_selected === true  -> OPTION 1: "Thank you for downloading…" overlay. Close keeps the
//                             user on the VISINVOKE dashboard (no redirect). An optional
//                             "install help" panel (steps + guide image) can be expanded.
//   dcc_selected === false -> OPTION 2: "…you will require Desktop Command Centre…" overlay with
//                             a button to the DCC download page (user-initiated, stays in-website).
//
// This change is UI/content only: the two-outcome decision, the routing, and the account state
// it reads are all unchanged. OPTION 2 is untouched. A module-scoped guard shows it once per
// page load (no storage), so it appears on dashboard entry without re-popping on internal
// navigation. A full reload re-reads the account state.
let hasRunThisLoad = false;

export default function DccVerificationOverlay() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (hasRunThisLoad) return;
    if (!user || !user.onboarding) return; // wait for authoritative account state
    hasRunThisLoad = true;
    // A connected DCC (recorded server-side by the backend on desktop token exchange) means the user
    // has actually installed and signed into the DCC. Never show the download-requirement overlay then.
    if (user.onboarding.dcc_connected === true) return;
    setSelected(user.onboarding.dcc_selected === true);
    setShow(true);
  }, [user]);

  if (!show) return null;

  return (
    <div style={s.backdrop} role="dialog" aria-modal="true" aria-label="Desktop Command Centre">
      <div style={s.card}>
        <div style={s.logo}>VISINVOKE</div>
        {selected ? (
          // OPTION 1 — user selected DCC download
          <>
            <h2 style={s.title}>Desktop Command Centre</h2>
            <p style={s.body}>
              Thank you for downloading Desktop Command Centre. Please install DCC and sign in to use our services.
            </p>

            <button
              type="button"
              style={s.helpToggle}
              onClick={() => setShowHelp(v => !v)}
              aria-expanded={showHelp}
            >
              {showHelp ? 'Hide install steps' : 'Need help installing? View the steps'}
            </button>

            {showHelp && (
              <div style={s.help}>
                <ol style={s.steps}>
                  {DCC_INSTALL_STEPS.map((step, i) => (
                    <li key={i} style={s.step}>{step}</li>
                  ))}
                </ol>
                <img src={DCC_SMARTSCREEN_IMG} alt="Windows installation confirmation screen" style={s.shot} />
                <p style={s.caption}>{DCC_SMARTSCREEN_CAPTION}</p>
              </div>
            )}

            <button style={s.btn} type="button" onClick={() => setShow(false)}>
              Close
            </button>
          </>
        ) : (
          // OPTION 2 — user did not select DCC download (unchanged approved flow)
          <>
            <h2 style={s.title}>Desktop Command Centre required</h2>
            <p style={s.body}>
              As part of the VISINVOKE system you will require Desktop Command Centre to access the majority of our services. Please click here to go to the download page.
            </p>
            <button style={s.btn} type="button" onClick={() => { setShow(false); navigate('/desktop-command-center'); }}>
              Go to the download page
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 4000,
    background: 'rgba(13, 17, 23, 0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  card: {
    background: '#fff', borderRadius: 14, padding: '34px 32px', width: '100%', maxWidth: 460,
    boxShadow: '0 24px 70px rgba(0,0,0,0.45)', textAlign: 'center',
    maxHeight: '90vh', overflowY: 'auto',
  },
  logo: { fontWeight: 900, fontSize: 20, color: '#8b5cf6', marginBottom: 10, letterSpacing: 0.5 },
  title: { margin: '0 0 12px', fontSize: 21, fontWeight: 800, color: '#1a1a1a' },
  body: { margin: '0 0 16px', fontSize: 14.5, color: '#4b4b57', lineHeight: 1.6 },
  helpToggle: {
    background: 'none', border: 'none', color: '#8b5cf6', fontWeight: 700, fontSize: 13.5,
    cursor: 'pointer', padding: 0, marginBottom: 14, textDecoration: 'underline',
  },
  help: {
    textAlign: 'left', background: '#f7f5ff', border: '1px solid #e6ddff', borderRadius: 10,
    padding: '16px 18px', marginBottom: 18,
  },
  steps: { margin: '0 0 12px', paddingLeft: 20 },
  step: { fontSize: 13, color: '#4b4b57', lineHeight: 1.7, marginBottom: 2 },
  shot: {
    display: 'block', width: '100%', height: 'auto', borderRadius: 8,
    border: '1px solid #e6ddff', boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
  },
  caption: { margin: '10px 0 0', fontSize: 12, color: '#6b6b78', lineHeight: 1.55 },
  btn: {
    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none',
    borderRadius: 9, padding: '12px 26px', fontWeight: 700, fontSize: 14.5, cursor: 'pointer',
  },
};
